import { Link } from 'react-router-dom';
import Navigation from './components/Navigation';
import SEO from './components/SEO';
import { SiteStructuredData } from './components/StructuredData';
import { caseStudies } from './casestudies/caseStudiesData';
import CaseStudyCard from './components/CaseStudyCard';
import SiteFooter from './components/SiteFooter';

function HomePage() {
  const featuredCaseStudies = [
    '/case-studies/daily-market-report-sigma',
    '/case-studies/dtc-brand-portfolio',
    '/case-studies/dtc-apparel-brand',
    '/case-studies/national-legal-organization',
  ].map((href) => caseStudies.find((item) => href.endsWith(item.slug))!);

  return (
    <div className="min-h-screen bg-white font-['Inter'] overflow-x-hidden">
      <SEO
        title="Snowflake and Sigma consulting | Greyfield Data"
        description="Greyfield Data builds and repairs Snowflake data platforms, dbt models, Sigma workbooks, and reporting systems."
        url="https://www.greyfielddata.com"
      />
      <SiteStructuredData />
      <Navigation />

      {/* Hero Section - Dark */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-32 overflow-hidden bg-[#0a1628]">
        {/* Animated gradient background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-blue-600/20 via-indigo-600/10 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s' }} />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-violet-600/15 via-purple-600/10 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '10s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl" />
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <p className="mb-5 text-sm font-semibold uppercase tracking-[0.18em] text-blue-300">Snowflake and Sigma consulting</p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold text-white leading-[1.15] mb-8 tracking-tight">
              Data insights that{' '}
              <span className="block bg-gradient-to-r from-blue-400 via-cyan-400 to-violet-400 bg-clip-text text-transparent">
                pay for themselves
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-400 leading-relaxed mb-12 max-w-2xl">
              We bring your warehouse, pipelines, models, and dashboards together so your team can trust the numbers.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://cal.com/linustse/meeting-with-linus?overlayCalendar=true"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 bg-white text-[#0a1628] px-8 py-4 rounded-xl text-base font-semibold hover:bg-gray-100 transition shadow-xl shadow-white/10"
              >
                Schedule a free consultation
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a
                href="#work"
                className="inline-flex items-center justify-center gap-2 bg-white/5 text-white px-8 py-4 rounded-xl text-base font-medium border border-white/10 hover:bg-white/10 hover:border-white/20 transition backdrop-blur"
              >
                See the work
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Light */}
      <section className="py-24 md:py-32 bg-white scroll-mt-20" id="services">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#1a4e94] tracking-wide uppercase mb-3">What we do</p>
            <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] text-gray-900 tracking-tight mb-4">
              From raw systems to the numbers people use
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">
              Each engagement starts with a specific question, report, workflow, or warehouse cost problem.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid md:grid-cols-3 gap-4">
            {/* Large card - Analytics */}
            <Link to="/services/sigma-consulting" className="md:col-span-2 bg-gradient-to-br from-[#173f78] to-[#0a1628] rounded-3xl p-8 md:p-10 text-white relative overflow-hidden group ring-1 ring-inset ring-white/10 transition-transform hover:-translate-y-0.5">
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px]" />
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
              <div className="relative z-10 flex h-full flex-col">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-blue-200">01 / Analytics</p>
                <h3 className="mb-4 text-2xl font-semibold">Sigma consulting</h3>
                <p className="text-white/70 text-lg leading-relaxed max-w-lg">
                  Sigma workbooks and dashboards connected to Snowflake models, with filters, totals, and definitions checked before publishing.
                </p>
                <span className="mt-auto inline-flex pt-6 font-semibold text-blue-200">See Sigma consulting →</span>
              </div>
            </Link>

            {/* Small card - Data Engineering */}
            <Link to="/services/snowflake-consulting" className="group flex flex-col rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_18px_45px_-38px_rgba(15,23,42,0.65)] transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg md:p-10">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#1a4e94]">02 / Data platform</p>
              <h3 className="mb-4 text-2xl font-semibold text-gray-900">Snowflake consulting</h3>
              <p className="text-lg leading-relaxed text-gray-500">
                Account setup, pipelines, dbt models, cost controls, and reporting models.
              </p>
              <span className="mt-auto inline-flex pt-6 font-semibold text-[#1a4e94]">See Snowflake consulting →</span>
            </Link>

            {/* Small card - Custom Apps */}
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_18px_45px_-38px_rgba(15,23,42,0.65)] transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#1a4e94]">03 / Applications</p>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Custom applications</h3>
              <p className="text-gray-500 leading-relaxed">
                Custom portals, internal tools, and web apps built around the steps your team already follows.
              </p>
            </div>

            {/* Large card - Fractional Team */}
            <div className="md:col-span-2 rounded-3xl border border-blue-100 bg-[#f7faff] p-8 md:p-10 shadow-[0_18px_45px_-38px_rgba(15,23,42,0.65)] transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#1a4e94]">04 / Embedded support</p>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Fractional data team</h3>
              <p className="text-gray-500 leading-relaxed">
                Ongoing data leadership, support, and hands-on work alongside your team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Case studies Section - Light gray */}
      <section className="py-24 md:py-32 bg-gray-50 scroll-mt-20" id="work">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#1a4e94] tracking-wide uppercase mb-3">Case studies</p>
            <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] text-gray-900 tracking-tight mb-4">
              Reports and models in production
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">
              Sigma daily reports, Snowflake and dbt models, Power BI operations, and customer-facing Tableau tools.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {featuredCaseStudies.map((study) => (
              <CaseStudyCard key={study.slug} study={study} />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/case-studies"
              className="inline-flex min-h-[44px] items-center gap-2 text-base font-semibold text-[#1a4e94] hover:text-[#0f2d54] transition"
            >
              View all case studies
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries Section - Light */}
      <section className="py-24 md:py-32 bg-white scroll-mt-20" id="industries">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#1a4e94] tracking-wide uppercase mb-3">Industries</p>
            <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] text-gray-900 tracking-tight mb-4">
              Work by industry
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">
              Recent work includes infusion clinics, ecommerce brands, healthcare, manufacturing, professional services, financial services, and legal organizations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Infusion centers */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#173f78] to-[#0a1628] p-8 text-white ring-1 ring-inset ring-white/10 md:p-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />
              <div className="relative z-10">
                <p className="mb-8 text-xs font-semibold uppercase tracking-[0.18em] text-blue-200">01 / Healthcare delivery</p>
                <h3 className="text-2xl font-semibold mb-4">Infusion centers</h3>
                <p className="text-white/70 mb-6 leading-relaxed">
                  We build patient flow reports, treatment tracking, and physician portals for infusion clinics.
                </p>
                <div className="divide-y divide-white/10 border-y border-white/10 text-white/80">
                  <p className="py-3">Patient schedules and chair use</p>
                  <p className="py-3">Treatment records and outcomes</p>
                  <p className="py-3">Staff and chair use reporting</p>
                </div>
              </div>
            </div>

            {/* eCommerce */}
            <div className="relative overflow-hidden rounded-3xl border border-blue-100 bg-[#f7faff] p-8 text-slate-950 shadow-[0_18px_45px_-38px_rgba(15,23,42,0.65)] md:p-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100/60 rounded-full blur-3xl" />
              <div className="absolute inset-0 bg-[linear-gradient(rgba(26,78,148,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(26,78,148,0.025)_1px,transparent_1px)] bg-[size:40px_40px]" />
              <div className="relative z-10">
                <p className="mb-8 text-xs font-semibold uppercase tracking-[0.18em] text-[#1a4e94]">02 / Commerce data</p>
                <h3 className="text-2xl font-semibold mb-4">Ecommerce</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  We combine Amazon, Shopify, TikTok Shop, ad, inventory, and fulfillment data for channel and product reporting.
                </p>
                <div className="divide-y divide-blue-100 border-y border-blue-100 text-slate-700">
                  <p className="py-3">Amazon, Shopify, and TikTok Shop data</p>
                  <p className="py-3">Marketing attribution</p>
                  <p className="py-3">Inventory and fulfillment reporting</p>
                </div>
              </div>
            </div>
          </div>

          {/* Other Industries */}
          <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-gray-400 mb-3">We also work with</p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm font-medium text-gray-600 bg-white px-4 py-2 rounded-lg border border-gray-200">Healthcare</span>
                  <span className="text-sm font-medium text-gray-600 bg-white px-4 py-2 rounded-lg border border-gray-200">Manufacturing</span>
                  <span className="text-sm font-medium text-gray-600 bg-white px-4 py-2 rounded-lg border border-gray-200">Professional services</span>
                  <span className="text-sm font-medium text-gray-600 bg-white px-4 py-2 rounded-lg border border-gray-200">Financial services</span>
                  <span className="text-sm font-medium text-gray-600 bg-white px-4 py-2 rounded-lg border border-gray-200">Legal</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* White paper - Light */}
      <section aria-labelledby="white-paper-heading" className="bg-white pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-3xl border border-blue-100 bg-[#f7faff] p-8 shadow-[0_18px_45px_-38px_rgba(15,23,42,0.65)] md:p-14">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100/60 rounded-full blur-3xl" />
            <div className="relative z-10 grid items-center gap-10 md:grid-cols-[220px_minmax(0,1fr)] lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-16">
              <Link to="/white-papers/ecommerce-data-stack" className="mx-auto block w-40 md:w-full md:max-w-[240px]">
                <img
                  src="/images/downloads/ecommerce-data-stack-white-paper-cover.webp"
                  alt="Cover of the white paper The Ecommerce and Consumer Brand Data Stack"
                  width={816}
                  height={1056}
                  loading="lazy"
                  className="h-auto w-full rounded-lg border border-blue-100 shadow-[0_30px_60px_-30px_rgba(10,22,40,0.55)]"
                />
              </Link>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1a4e94]">Free white paper</p>
                <h2 id="white-paper-heading" className="mt-4 text-3xl md:text-4xl lg:text-[2.75rem] font-semibold leading-[1.1] tracking-tight text-gray-900">
                  The Ecommerce and Consumer Brand Data Stack
                </h2>
                <p className="mt-5 max-w-2xl text-lg md:text-xl leading-relaxed text-gray-500">
                  Which tools to pick at your brand's stage, and a five-phase plan to build them, in 18 pages.
                </p>
                <div className="mt-8 flex flex-col gap-5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6">
                  <Link
                    to="/white-papers/ecommerce-data-stack"
                    className="group inline-flex items-center justify-center gap-2 rounded-xl bg-[#0a1628] px-8 py-4 text-base font-semibold text-white transition hover:bg-[#173f78]"
                  >
                    Get the free white paper
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                  <p className="text-[15px] text-gray-500">
                    Also free:{' '}
                    <Link to="/white-papers/customer-retention-reporting" className="font-semibold text-[#1a4e94] underline underline-offset-4 hover:text-[#0f2d54]">
                      Customer Retention Reporting for Consumer Brands
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Dark */}
      <section className="py-24 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-[radial-gradient(circle_at_top_left,_#173f78_0,_#0a192f_44%,_#071525_100%)] p-12 ring-1 ring-inset ring-white/10 md:p-20">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px]" />

            <div className="relative z-10 text-center">
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">Start with a real problem</p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.1] text-white mb-6 tracking-tight">
                Ready to unlock your data?
              </h2>
              <p className="text-xl text-blue-100/70 mb-10 max-w-2xl mx-auto">
                Book a free consultation. We'll discuss your challenges, share relevant ideas, and figure out if we're a good fit.
              </p>
              <a
                href="https://cal.com/linustse/meeting-with-linus?overlayCalendar=true"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 bg-white text-[#1a4e94] px-10 py-5 rounded-xl text-lg font-semibold hover:bg-gray-100 transition shadow-2xl shadow-black/20"
              >
                Schedule Your Free Call
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

export default HomePage;
